import { FC, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { decryptString } from "../../../axios/axiosEncryptionUtils";
import { clearSsoSlice, updateSSOSlice } from "../Sso_Slice";
import {
  useAppDispatch,
  useAppSelector,
} from "../../../redux-store/CustomReduxHooks";
import {
  saveCsUserId,
  updateChannelDetails,
  updateTraceId,
} from "../../../redux-store/reducers/CsAppSlice";
import { J_FunctionalityCode } from "../../../globalTypes/GlobalTypes";
import { T_ValidateSSOOldRes } from "../Sso_Types";
import { updateLoginData } from "../../Feature_Login/Login_Slice";
import { isEmpty } from "lodash";
import NotFound from "../../../pages/NotFound/NotFound";
import { InAppFeatureFuncCodes } from "../../../utils/constants";
import CsSessionStorageUtils from "../../../utils/CsSessionStorageUtils";
import { R_HiRoutes } from "../../../utils/Routes";
import { updateLoadingState } from "../../Feature_Loading/Loading_Slice";
import { getChannelBySourceLobId } from "../../../utils/conversionUtils";

const ValidateSsoOld: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const appDispatch = useAppDispatch();
  const { tokenDetails } = useAppSelector((state) => state.CsAppSlice);
  const [isError, setIsError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<any>("");

  const validateSsoResponseWithConfig = (configurationData: any) => {
    const searchData = location.search;
    const responseParam = searchData.split("response=");
    if (responseParam.length <= 1 || !responseParam[1]) {
      throw new Error("Missing SSO response payload");
    }

    if (!configurationData?.iv || !configurationData?.key) {
      throw new Error("Invalid SSO configuration data");
    }

    const response = responseParam[1];
    const decryptedValue = decryptString(
      response,
      configurationData.iv,
      configurationData.key,
    );

    const jsonValue = JSON.parse(decryptedValue);
    const validateSsoOldData = jsonValue as T_ValidateSSOOldRes;
    validateSsoOldData.appVersion = "";

    let channel = getChannelBySourceLobId(
      validateSsoOldData?.sourceLobId || "",
    );
    appDispatch(updateChannelDetails(channel));

    if (validateSsoOldData?.traceId && !isEmpty(validateSsoOldData.traceId)) {
      CsSessionStorageUtils.set("TRACE_ID", validateSsoOldData.traceId);
      appDispatch(updateTraceId(validateSsoOldData.traceId));
    }

    if (validateSsoOldData?.csUserId && !isEmpty(validateSsoOldData.csUserId)) {
      appDispatch(saveCsUserId(validateSsoOldData.csUserId));
    }

    appDispatch(
      updateLoginData({
        key: "isUserLoggedIn",
        value: true,
      }),
    );

    const routeDetails = J_FunctionalityCode.find(
      (f) => f.funcCode === validateSsoOldData.functionality,
    );

    if (!routeDetails?.route) {
      throw new Error("Unable to map functionality to route");
    }

    // Override 06PR(Policy Renewals) route for SSO flow from ABCD->ABCS, doing for OSP-7497
    const targetRoute =
      validateSsoOldData.functionality ===
      InAppFeatureFuncCodes.HI_POLICY_RENEWAL
        ? R_HiRoutes.details_PolicyNumber
        : routeDetails.route;

    const navStateData: any = {};
    routeDetails?.dynamicKeys.forEach((d) => {
      if (["policyNumber", "policyNo"].includes(d)) {
        navStateData[d] = jsonValue[d] || jsonValue["policyNo"];
      } else {
        navStateData[d] = jsonValue[d] || "";
      }
    });

    validateSsoOldData.lobCode = routeDetails?.lobCode || [];
    validateSsoOldData.urlConstructured = "" + targetRoute;
    validateSsoOldData.navigationParams = JSON.stringify(navStateData);
    appDispatch(updateSSOSlice(validateSsoOldData));
    navigate("" + validateSsoOldData.urlConstructured, {
      state: { ...navStateData },
      replace: true,
    });
  };

  useEffect(() => {
    try {
      if (tokenDetails?.configuration?.iv) {
        try {
          validateSsoResponseWithConfig(tokenDetails?.configuration);
        } catch (error) {
          console.error(
            "Error validating SSO response with configuration",
            error,
          );
        }
      } else {
        appDispatch(updateLoadingState(true));
      }
    } catch (e: any) {
      appDispatch(clearSsoSlice());
      appDispatch(updateLoadingState(false));
      setIsError(true);
      setErrorMessage(e);
    }
  }, [tokenDetails?.configuration]);

  return isError ? (
    <NotFound
      hideButton={true}
      moduleName="SSO"
      pageName="ValidateSSOToken"
      errorTitle=""
      errorMessage={errorMessage}
    />
  ) : (
    <></>
  );
};
export default ValidateSsoOld;
